import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaClock, FaCheckCircle, FaTimesCircle, FaSpinner, FaFilePdf, FaFileWord, FaTrash, FaUser, FaEdit, FaPlus, FaFileUpload, FaTimes, FaUserTie, FaCalendarAlt, FaHourglassHalf, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';

const MyPaper = () => {
  const [papers, setPapers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paperToDelete, setPaperToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [paperToEdit, setPaperToEdit] = useState(null);
  const [editUploadedFile, setEditUploadedFile] = useState(null);
  const [toast, setToast] = useState(null);
  const [showReviewCommentModal, setShowReviewCommentModal] = useState(false);
  const [selectedReviewComment, setSelectedReviewComment] = useState('');
  const [reviewCommentType, setReviewCommentType] = useState(''); // 'accepted' or 'rejected'
  const [acceptedAbstracts, setAcceptedAbstracts] = useState([]);
  const navigate = useNavigate();

  const subjects = [
    'Computer Science',
    'Electronics',
    'Mechanical Engineering',
    'Civil Engineering',
    'Electrical Engineering',
    'Physics',
    'Chemistry',
    'Mathematics',
    'Biology',
    'Commerce',
    'Arts',
    'Other',
  ];

  const majorsBySubject = {
    'Computer Science': ['Software Engineering', 'Artificial Intelligence', 'Data Science', 'Cyber Security', 'Web Development'],
    'Electronics': ['VLSI Design', 'Embedded Systems', 'Communication Systems', 'Signal Processing'],
    'Mechanical Engineering': ['Thermal Engineering', 'Manufacturing', 'Automotive', 'Robotics'],
    'Physics': ['Quantum Physics', 'Nuclear Physics', 'Astrophysics', 'Applied Physics'],
    'Chemistry': ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Analytical Chemistry'],
    'Mathematics': ['Pure Mathematics', 'Applied Mathematics', 'Statistics', 'Operations Research'],
  };

  const universities = [
    'Delhi University',
    'Mumbai University',
    'Jawaharlal Nehru University',
    'Banaras Hindu University',
    'Aligarh Muslim University',
    'University of Calcutta',
    'University of Madras',
    'Pune University',
    'Anna University',
    'Other',
  ];

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    loadPapers();
  }, [navigate]);

  const loadPapers = () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) return;

    const allPapers = JSON.parse(localStorage.getItem('papers') || '[]');
    const userPapers = allPapers.filter(paper => paper.userId === currentUser.id);

    // Sort by submission date in descending order (LIFO - newest first)
    const sortedPapers = userPapers.sort((a, b) => {
      const dateA = new Date(a.submittedAt);
      const dateB = new Date(b.submittedAt);
      return dateB - dateA; // Descending order (newest first)
    });

    setPapers(sortedPapers);

    // Load accepted abstracts for the user
    const allAbstracts = JSON.parse(localStorage.getItem('abstracts') || '[]');
    const userAcceptedAbstracts = allAbstracts.filter(
      abstract => abstract.userId === currentUser.id && abstract.status === 'accepted'
    );
    setAcceptedAbstracts(userAcceptedAbstracts);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'submitted':
        return {
          label: 'Submitted',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          icon: FaClock,
          borderColor: 'border-blue-500'
        };
      case 'in_queue':
        return {
          label: 'In Queue',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-700',
          icon: FaHourglassHalf,
          borderColor: 'border-orange-500'
        };
      case 'in_review':
        return {
          label: 'In Review',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-700',
          icon: FaSpinner,
          borderColor: 'border-yellow-500'
        };
      case 'accepted':
        return {
          label: 'Accepted',
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          icon: FaCheckCircle,
          borderColor: 'border-green-500'
        };
      case 'rejected':
        return {
          label: 'Rejected',
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          icon: FaTimesCircle,
          borderColor: 'border-red-500'
        };
      default:
        return {
          label: 'Unknown',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          icon: FaClock,
          borderColor: 'border-gray-500'
        };
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return null;
    const extension = fileName.split('.').pop().toLowerCase();
    if (extension === 'pdf') {
      return <FaFilePdf className="text-red-500 text-xl" />;
    } else if (extension === 'doc' || extension === 'docx') {
      return <FaFileWord className="text-blue-500 text-xl" />;
    }
    return <FaFileAlt className="text-gray-500 text-xl" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleReviewCommentClick = (paper) => {
    if (paper.reviewComment && paper.reviewComment.trim()) {
      setSelectedReviewComment(paper.reviewComment);
      setReviewCommentType(paper.status); // 'accepted' or 'rejected'
      setShowReviewCommentModal(true);
    }
  };

  const handleEditClick = (paper) => {
    const authorsWithIds = (paper.authors || []).map((a, idx) => ({
      ...a,
      id: a.id || `author-${Date.now()}-${idx}`
    }));
    setPaperToEdit({ ...paper, authors: authorsWithIds });
    setEditUploadedFile(paper.uploadedFile || null);
    setShowEditModal(true);
  };

  const handleDeleteClick = (paper) => {
    setPaperToDelete(paper);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (paperToDelete) {
      const allPapers = JSON.parse(localStorage.getItem('papers') || '[]');
      const updatedPapers = allPapers.filter(paper => paper.id !== paperToDelete.id);
      localStorage.setItem('papers', JSON.stringify(updatedPapers));

      loadPapers();
      setShowDeleteConfirm(false);
      setPaperToDelete(null);
      showToast('Paper deleted successfully', 'success');
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPaperToDelete(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setEditUploadedFile(file);
      } else {
        showToast('Please upload only PDF or Word documents', 'error');
        e.target.value = '';
      }
    }
  };

  const removeEditFile = () => {
    setEditUploadedFile(null);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();

    if (!editUploadedFile) {
      showToast('Please upload a paper document', 'error');
      return;
    }

    const updatedPaper = {
      ...paperToEdit,
      uploadedFile: {
        name: editUploadedFile.name,
        type: editUploadedFile.type,
        size: editUploadedFile.size
      },
      contentType: 'file'
    };

    const allPapers = JSON.parse(localStorage.getItem('papers') || '[]');
    const updatedPapers = allPapers.map(paper =>
      paper.id === updatedPaper.id ? updatedPaper : paper
    );
    localStorage.setItem('papers', JSON.stringify(updatedPapers));

    loadPapers();
    setShowEditModal(false);
    setPaperToEdit(null);
    setEditUploadedFile(null);
    showToast('Paper updated successfully', 'success');
  };

  const handleEditChange = (field, value) => {
    setPaperToEdit(prev => ({ ...prev, [field]: value }));
  };

  const handleAuthorChange = (id, field, value) => {
    setPaperToEdit(prev => {
      const updatedAuthors = prev.authors.map(author =>
        author.id === id ? { ...author, [field]: value } : author
      );
      return { ...prev, authors: updatedAuthors };
    });
  };

  const addAuthor = () => {
    const newId = `author-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    setPaperToEdit(prev => ({
      ...prev,
      authors: [...prev.authors, { id: newId, name: '', email: '', university: '' }]
    }));
  };

  const removeAuthor = (id) => {
    if (paperToEdit.authors.length > 1) {
      const updatedAuthors = paperToEdit.authors.filter(author => author.id !== id);
      setPaperToEdit({ ...paperToEdit, authors: updatedAuthors });
    } else {
      showToast('At least one author is required', 'error');
    }
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all hover:border-gray-300 text-sm";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Review Comment Modal (for both Acceptance and Rejection) */}
      {showReviewCommentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full animate-fade-in">
            <div className={`p-6 rounded-t-2xl ${reviewCommentType === 'accepted'
              ? 'bg-gradient-to-r from-green-500 to-green-600'
              : 'bg-gradient-to-r from-red-500 to-red-600'
              }`}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {reviewCommentType === 'accepted' ? 'Acceptance Comments' : 'Rejection Comments'}
                  </h2>
                  <p className={reviewCommentType === 'accepted' ? 'text-green-100' : 'text-red-100'}>
                    Review comments from the evaluator
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewCommentModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Reviewer's Feedback</h3>
                <div className={`border-l-4 p-4 rounded-r-lg ${reviewCommentType === 'accepted'
                  ? 'bg-green-50 border-green-500'
                  : 'bg-red-50 border-red-500'
                  }`}>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedReviewComment}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowReviewCommentModal(false)}
                  className={`px-6 py-3 text-white rounded-lg font-semibold hover:shadow-lg transition-all ${reviewCommentType === 'accepted'
                    ? 'bg-gradient-to-r from-green-500 to-green-600'
                    : 'bg-gradient-to-r from-red-500 to-red-600'
                    }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && paperToEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary-blue to-secondary-blue p-6 rounded-t-2xl sticky top-0 z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Edit Paper</h2>
                  <p className="text-blue-100">Update your paper details</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditUploadedFile(null);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6">
              <div className="space-y-6">
                {/* Academic Information */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Academic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className={labelClass}>Subject <span className="text-red-500">*</span></label>
                      <select
                        value={paperToEdit.subject}
                        onChange={(e) => handleEditChange('subject', e.target.value)}
                        required
                        className={`${inputClass} bg-white`}
                      >
                        <option value="">Select Subject</option>
                        {subjects.map((sub) => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Major <span className="text-red-500">*</span></label>
                      <select
                        value={paperToEdit.major}
                        onChange={(e) => handleEditChange('major', e.target.value)}
                        required
                        disabled={!paperToEdit.subject || !majorsBySubject[paperToEdit.subject]}
                        className={`${inputClass} bg-white disabled:bg-gray-50`}
                      >
                        <option value="">Select Major</option>
                        {paperToEdit.subject && majorsBySubject[paperToEdit.subject]?.map((maj) => (
                          <option key={maj} value={maj}>{maj}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Specialization</label>
                      <input
                        type="text"
                        value={paperToEdit.specialization || ''}
                        onChange={(e) => handleEditChange('specialization', e.target.value)}
                        className={inputClass}
                        placeholder="Your specialization (optional)"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>University <span className="text-red-500">*</span></label>
                      <select
                        value={paperToEdit.university}
                        onChange={(e) => handleEditChange('university', e.target.value)}
                        required
                        className={`${inputClass} bg-white`}
                      >
                        <option value="">Select University</option>
                        {universities.map((uni) => (
                          <option key={uni} value={uni}>{uni}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Select Abstract - Only show if there are accepted abstracts */}
                {acceptedAbstracts.length > 0 && (
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Link Abstract (Optional)</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className={labelClass}>Select Accepted Abstract</label>
                        <select
                          value={paperToEdit.linkedAbstractId || ''}
                          onChange={(e) => handleEditChange('linkedAbstractId', e.target.value)}
                          className={`${inputClass} bg-white`}
                        >
                          <option value="">No Abstract Selected</option>
                          {acceptedAbstracts.map((abstract) => (
                            <option key={abstract.id} value={abstract.id}>
                              {abstract.abstractTitle}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-2">Link this paper to one of your accepted abstracts</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paper Details */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Paper Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Paper Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={paperToEdit.paperTitle}
                        onChange={(e) => handleEditChange('paperTitle', e.target.value)}
                        required
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Submission Date <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={paperToEdit.submissionDate}
                        onChange={(e) => handleEditChange('submissionDate', e.target.value)}
                        required
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Authors Information */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Authors Information</h3>
                  {paperToEdit.authors && paperToEdit.authors.map((author, index) => (
                    <div key={author.id} className="mb-4 p-4 bg-green-50/50 rounded-xl border-l-4 border-green-500">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-bold text-gray-800">Author {index + 1}</h4>
                        {paperToEdit.authors.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAuthor(author.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={labelClass}>Author Name <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={author.name}
                            onChange={(e) => handleAuthorChange(author.id, 'name', e.target.value)}
                            required
                            className={inputClass}
                            placeholder="Full name"
                          />
                        </div>

                        <div>
                          <label className={labelClass}>Author Email <span className="text-red-500">*</span></label>
                          <input
                            type="email"
                            value={author.email}
                            onChange={(e) => handleAuthorChange(author.id, 'email', e.target.value)}
                            required
                            className={inputClass}
                            placeholder="email@example.com"
                          />
                        </div>

                        <div>
                          <label className={labelClass}>Author University <span className="text-red-500">*</span></label>
                          <select
                            value={author.university}
                            onChange={(e) => handleAuthorChange(author.id, 'university', e.target.value)}
                            required
                            className={`${inputClass} bg-white`}
                          >
                            <option value="">Select University</option>
                            {universities.map((uni) => (
                              <option key={uni} value={uni}>{uni}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addAuthor}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md font-semibold"
                  >
                    <FaPlus />
                    Add Another Author
                  </button>
                </div>

                {/* Mentor Information */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Mentor Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className={labelClass}>Mentor Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={paperToEdit.mentorName || paperToEdit.guideName || ''}
                        onChange={(e) => handleEditChange('mentorName', e.target.value)}
                        required
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Designation <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={paperToEdit.mentorDesignation || paperToEdit.guideDesignation || ''}
                        onChange={(e) => handleEditChange('mentorDesignation', e.target.value)}
                        required
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Mentor University <span className="text-red-500">*</span></label>
                      <select
                        value={paperToEdit.mentorUniversity || paperToEdit.guideUniversity || ''}
                        onChange={(e) => handleEditChange('mentorUniversity', e.target.value)}
                        required
                        className={`${inputClass} bg-white`}
                      >
                        <option value="">Select University</option>
                        {universities.map((uni) => (
                          <option key={uni} value={uni}>{uni}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Mentor Phone <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        value={paperToEdit.mentorPhone || paperToEdit.guidePhone || ''}
                        onChange={(e) => handleEditChange('mentorPhone', e.target.value)}
                        required
                        maxLength="10"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Mentor Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        value={paperToEdit.mentorEmail || paperToEdit.guideEmail || ''}
                        onChange={(e) => handleEditChange('mentorEmail', e.target.value)}
                        required
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Paper Document - FILE ONLY */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Paper Document</h3>

                  {/* File Upload */}
                  {!editUploadedFile && (
                    <div className="mb-6">
                      <label className={labelClass}>Upload Document (PDF or Word) <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileUpload}
                          className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100 cursor-pointer"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Supported formats: PDF, DOC, DOCX</p>
                    </div>
                  )}

                  {/* Display uploaded file with remove option */}
                  {editUploadedFile && (
                    <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                            <FaFileUpload className="text-white text-xl" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{editUploadedFile.name}</p>
                            <p className="text-sm text-gray-600">
                              {editUploadedFile.size ? (editUploadedFile.size / 1024).toFixed(2) + ' KB' : 'File'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeEditFile}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                          title="Remove file"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className={labelClass}>Keywords <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={paperToEdit.keywords}
                      onChange={(e) => handleEditChange('keywords', e.target.value)}
                      required
                      className={inputClass}
                      placeholder="Separate keywords with commas"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditUploadedFile(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete Paper</h3>
              <p className="text-gray-600">Are you sure you want to delete this paper? This action cannot be undone.</p>
              {paperToDelete && (
                <p className="text-sm text-gray-500 mt-2 font-semibold">"{paperToDelete.paperTitle}"</p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">My Papers</h1>
                <p className="text-green-100">View and track your submitted papers</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaFileAlt className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Papers List */}
          {papers.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Papers Yet</h3>
              <p className="text-gray-600">You haven't submitted any papers for review.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {papers.map((paper) => {
                const statusConfig = getStatusConfig(paper.status);
                const StatusIcon = statusConfig.icon;
                const canEdit = paper.status === 'submitted';
                const hasReviewComment = (paper.status === 'accepted' || paper.status === 'rejected') && paper.reviewComment && paper.reviewComment.trim();

                return (
                  <div
                    key={paper.id}
                    className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border-l-4 ${statusConfig.borderColor}`}
                  >
                    <div className="p-6">
                      {/* Status and Dates Timeline - Status now at top-right corner */}
                      <div className="flex flex-wrap justify-between items-start mb-6">
                        {/* Left: All Dates */}
                        <div className="flex flex-wrap items-center gap-4">
                          {/* All Dates in Same Line */}
                          <div className="flex flex-wrap gap-4 text-xm text-gray-1000">
                            {paper.submittedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>Submitted: {formatDate(paper.submittedAt)}</span>
                              </div>
                            )}
                            {paper.inQueueAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>In Queue: {formatDate(paper.inQueueAt)}</span>
                              </div>
                            )}
                            {paper.inReviewAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>In Review: {formatDate(paper.inReviewAt)}</span>
                              </div>
                            )}
                            {paper.acceptedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>Accepted: {formatDate(paper.acceptedAt)}</span>
                              </div>
                            )}
                            {paper.rejectedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>Rejected: {formatDate(paper.rejectedAt)}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right: Status Badge with Eye icon for accepted/rejected status with comments */}
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${statusConfig.bgColor} ${statusConfig.textColor} text-sm font-semibold`}>
                          <StatusIcon className={paper.status === 'in_review' ? 'animate-spin' : ''} />
                          {statusConfig.label}

                          {/* Eye icon for accepted/rejected status with comments */}
                          {hasReviewComment && (
                            <button
                              onClick={() => handleReviewCommentClick(paper)}
                              className={`ml-2 transition-colors ${paper.status === 'accepted'
                                ? 'text-green-600 hover:text-green-800'
                                : 'text-red-600 hover:text-red-800'
                                }`}
                              title={`View ${paper.status === 'accepted' ? 'acceptance' : 'rejection'} comments`}
                            >
                              <FaEye />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Main Info Row - Flexible layout that auto-adjusts to fill space */}
                      <div className="flex flex-wrap gap-4 mb-4">
                        {/* 1. Paper Title - Takes more space */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border-2 border-green-200 flex-[2_1_250px] min-w-[250px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Paper Title</p>
                          <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={paper.paperTitle}>
                            {paper.paperTitle}
                          </p>
                        </div>

                        {/* 2. Subject */}
                        <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Subject</p>
                          <p className="font-semibold text-gray-900 text-sm">{paper.subject}</p>
                        </div>

                        {/* 3. Major */}
                        <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Major</p>
                          <p className="font-semibold text-gray-900 text-sm">{paper.major}</p>
                        </div>

                        {/* 4. Specialization - Only show if exists and not empty */}
                        {paper.specialization && paper.specialization.trim() && (
                          <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                            <p className="text-xs text-gray-500 mb-1 font-semibold">Specialization</p>
                            <p className="font-semibold text-gray-900 text-sm">{paper.specialization}</p>
                          </div>
                        )}

                        {/* 5. Linked Abstract - Only show if linked */}
                        {paper.linkedAbstractId && (() => {
                          // eslint-disable-next-line eqeqeq
                          const linkedAbstract = acceptedAbstracts.find(abs => abs.id == paper.linkedAbstractId);
                          return linkedAbstract ? (
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg border-2 border-indigo-200 flex-[1_1_180px] min-w-[180px]">
                              <p className="text-xs text-gray-500 mb-1 font-semibold">Linked Abstract</p>
                              <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={linkedAbstract.abstractTitle}>
                                {linkedAbstract.abstractTitle}
                              </p>
                            </div>
                          ) : null;
                        })()}

                        {/* 6. Authors */}
                        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-3 rounded-lg border-2 border-teal-200 flex-[1_1_180px] min-w-[180px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Authors</p>
                          <div className="flex items-center gap-2">
                            <FaUser className="text-teal-500 text-sm" />
                            <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={paper.authors && paper.authors.length > 0 ? paper.authors.map(a => a.name).join(', ') : 'N/A'}>
                              {paper.authors && paper.authors.length > 0
                                ? paper.authors.map(a => a.name).join(', ')
                                : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* 7. Content Type - Paper always has file */}
                        {paper.uploadedFile && (
                          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 rounded-lg border-2 border-purple-200 flex-[1_1_180px] min-w-[180px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Submitted File</p>
                            <div className="flex items-center gap-2">
                              {getFileIcon(paper.uploadedFile.name)}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-900 truncate" title={paper.uploadedFile.name}>
                                  {paper.uploadedFile.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 8. Assigned Professor - ONLY if assigned */}
                        {paper.assignedTo && paper.assignedTo.professorName && (
                          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-3 rounded-lg border-2 border-amber-200 flex-[1_1_180px] min-w-[180px]">
                            <p className="text-xs text-gray-500 mb-1 font-semibold">Assigned Professor</p>
                            <div className="flex items-center gap-2">
                              <FaUserTie className="text-amber-600 text-sm" />
                              <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={paper.assignedTo.professorName}>
                                {paper.assignedTo.professorName}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-3">
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(paper)}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                          >
                            <FaEdit />
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteClick(paper)}
                          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                        >
                          <FaTrash />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyPaper;